import {
  adminSendTestNotificationResponseSchema,
  type AdminSendTestNotificationRequest,
  type AdminSendTestNotificationResponse,
  adminNotificationStatsSchema,
  adminRetryNotificationsResponseSchema,
  type AdminNotificationFilter,
  type AdminNotificationStats,
  type AdminRetryNotificationsRequest,
  type AdminRetryNotificationsResponse,
  adminPreviewNotificationTemplateResponseSchema,
  notificationTemplateListResponseSchema,
  notificationTemplateSchema,
  type AdminPreviewNotificationTemplateResponse,
  type AdminUpdateNotificationTemplateRequest,
  type NotificationTemplate,
  type NotificationTemplateListResponse,
  ADMIN_STATS_CACHE_SECONDS,
  ADMIN_USER_LOOKUP_AUDIT_WINDOW_MS,
  adminAccountListResponseSchema,
  adminAccountRowSchema,
  adminBannerListResponseSchema,
  adminBannerSchema,
  adminDegradedModeStatusSchema,
  broadcastOverlayAccessSchema,
  adminDrawDetailSchema,
  adminDrawListResponseSchema,
  adminDrawSchema,
  adminFraudTicketListResponseSchema,
  adminNotificationListResponseSchema,
  adminPhotoDetailSchema,
  adminPhotoListResponseSchema,
  adminReceiptDetailSchema,
  adminReceiptListResponseSchema,
  adminRetryFailedReceiptsResponseSchema,
  adminRewardItemSchema,
  adminRewardListResponseSchema,
  adminRewardRedemptionListResponseSchema,
  adminRewardRedemptionStatsSchema,
  adminStatsSchema,
  announceWinnersResponseSchema,
  adminTicketDetailSchema,
  adminUploadResponseSchema,
  adminLedgerRowSchema,
  adminUserDetailSchema,
  adminUserSearchResponseSchema,
  adminUserSummarySchema,
  dlqListResponseSchema,
  drawRevealStepSchema,
  runDrawResponseSchema,
  ticketModerationResponseSchema,
  type AdminAccountListResponse,
  type AdminAccountRow,
  type AdminBanner,
  type AdminBannerCreate,
  type AdminBannerListResponse,
  type AdminBannerUpdate,
  type AdminDegradedModeStatus,
  type AdminDraw,
  type AdminDrawDetail,
  type AdminDrawListResponse,
  type AdminFraudTicketListResponse,
  type AdminNotificationListResponse,
  type AdminPhotoDetail,
  type AdminPhotoModerationRequest,
  type AdminPhotoListResponse,
  type AdminReceiptDetail,
  type AdminReceiptListResponse,
  type AdminRetryFailedReceiptsRequest,
  type AdminRetryFailedReceiptsResponse,
  type AdminCancelRewardRedemptionRequest,
  type AdminDeliverRewardRedemptionRequest,
  type AdminRewardCreate,
  type AdminRewardItem,
  type AdminRewardRedemptionFilter,
  type AdminRewardRedemptionListResponse,
  type AdminRewardRedemptionStats,
  type AdminRewardListResponse,
  type AdminStats,
  type AdminTicketDetail,
  type AdminRewardUpdate,
  type AdminSetDegradedModeRequest,
  type AdminUploadKind,
  type AdminTicketGrantRequest,
  type AdminUserBlockRequest,
  type AdminUserDetail,
  type AdminUserPointsRequest,
  type AdminUserSummary,
  type AdminUserSearchResponse,
  type AnnounceWinnersResponse,
  type BroadcastOverlayAccess,
  type DlqListResponse,
  type DrawRevealKind,
  type DrawRevealStep,
  type OffsetPaginationQuery,
  type RunDrawResponse,
  type ScheduleDrawRequest,
  type TicketModerationRequest,
  type TicketModerationResponse,
} from '@qaytim/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { z } from 'zod';

import { AdminApiError, adminRequest } from './admin-api';

/**
 * `POST /admin/users/:id/points` va `.../tickets` javoblari.
 *
 * Shared da alohida kontrakt sifatida yashamaydi, chunki ularni faqat panel o'qiydi va ular
 * mavjud sxemalarning kombinatsiyasi — yangi nom qo'shish shartnoma lug'atini kattalashtirib,
 * hech narsa qo'shmasdi.
 */
const pointsAdjustResultSchema = z.object({
  user: adminUserSummarySchema,
  transaction: adminLedgerRowSchema,
});
export type AdminPointsAdjustResult = z.infer<typeof pointsAdjustResultSchema>;

const grantedTicketSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  status: z.string(),
  acquiredDate: z.string(),
  monthKey: z.string(),
});
export type AdminGrantedTicket = z.infer<typeof grantedTicketSchema>;

/**
 * Tortishuv boshqaruvining so'rovlari (§5 P3.5).
 *
 * Har bir mutatsiyadan keyin ro'yxat ham, ochiq konsol ham qayta o'qiladi. Sabab efir bilan
 * bog'liq: bosqich e'lon qilinganidan keyin ekranda ko'ringan holat serverdagi holat bilan bir
 * xil bo'lishi SHART — boshlovchi "e'lon qilindimi yo'qmi" degan savol bilan qolsa, u tugmani
 * ikkinchi marta bosadi.
 */
export const DRAWS_QUERY_KEY = ['admin', 'draws'] as const;
export const drawDetailQueryKey = (drawId: string) => ['admin', 'draws', drawId] as const;

export function useAdminDraws(page: OffsetPaginationQuery): UseQueryResult<AdminDrawListResponse> {
  return useQuery({
    queryKey: [...DRAWS_QUERY_KEY, 'list', page],
    queryFn: () =>
      adminRequest(`/admin/draws${toQuery({ ...page })}`, {
        schema: adminDrawListResponseSchema,
      }),
    // Sahifa almashganda eski jadval o'rnida turadi — ro'yxat "sakramaydi".
    placeholderData: (previous) => previous,
  });
}

export function useAdminDraw(drawId: string): UseQueryResult<AdminDrawDetail> {
  return useQuery({
    queryKey: drawDetailQueryKey(drawId),
    queryFn: () => adminRequest(`/admin/draws/${drawId}`, { schema: adminDrawDetailSchema }),
    // Efir davomida qo'shni ekranda ochilgan konsol eskirgan holatni ko'rsatmasligi uchun.
    staleTime: 0,
    refetchInterval: 1_000,
  });
}

/** Barcha tortishuv so'rovlarini bekor qiladi — ro'yxat va ochiq konsol birga yangilanadi. */
function useRefreshDraws(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: DRAWS_QUERY_KEY });
  };
}

export function useScheduleDraw(): UseMutationResult<AdminDraw, Error, ScheduleDrawRequest> {
  const refresh = useRefreshDraws();
  return useMutation({
    mutationFn: (body: ScheduleDrawRequest) =>
      adminRequest('/admin/draws/schedule', { method: 'POST', body, schema: adminDrawSchema }),
    onSuccess: refresh,
  });
}

/**
 * Efirni o'tkazish so'rovi — turi davr kalitini ham, endpointni ham belgilaydi.
 *
 * Uchta alohida shakl, bitta ixtiyoriy maydonlar to'plami emas: haftalik efirga `monthKey`
 * yuborish mumkin bo'lgan tur "hech qachon sodir bo'lmaydi" deb qoldirilgan xatoni imzoga
 * yozib qo'yardi.
 */
export type RunDrawInput =
  | { type: 'daily'; drawDate?: string }
  | { type: 'weekly'; weekKey?: string }
  | { type: 'monthly'; monthKey?: string; prizeLabel?: string };

export function useRunDraw(): UseMutationResult<RunDrawResponse, Error, RunDrawInput> {
  const refresh = useRefreshDraws();
  return useMutation({
    mutationFn: (input: RunDrawInput) => {
      switch (input.type) {
        case 'daily':
          return adminRequest('/admin/draws/daily/run', {
            method: 'POST',
            body: { drawDate: input.drawDate },
            schema: runDrawResponseSchema,
          });
        case 'weekly':
          return adminRequest('/admin/draws/weekly/run', {
            method: 'POST',
            body: { weekKey: input.weekKey },
            schema: runDrawResponseSchema,
          });
        case 'monthly':
          return adminRequest('/admin/draws/monthly/run', {
            method: 'POST',
            body: { monthKey: input.monthKey, prizeLabel: input.prizeLabel },
            schema: runDrawResponseSchema,
          });
      }
    },
    onSuccess: refresh,
  });
}

export function usePostponeDraw(): UseMutationResult<
  AdminDrawDetail,
  Error,
  { drawId: string; reason?: string }
> {
  const refresh = useRefreshDraws();
  return useMutation({
    mutationFn: ({ drawId, reason }: { drawId: string; reason?: string }) =>
      adminRequest(`/admin/draws/${drawId}/postpone`, {
        method: 'POST',
        body: { reason },
        schema: adminDrawDetailSchema,
      }),
    onSuccess: refresh,
  });
}

/**
 * Efirni mini appda ko'rsatish/yashirish.
 *
 * `usePostponeDraw` bilan yonma-yon turadi va ATAYIN alohida: qoldirish tortishuvni bekor
 * qiladi, bu esa faqat ekranni oladi. Ikkalasi bitta tugmaga birlashtirilsa, operator xato
 * vaqtni tuzatmoqchi bo'lib biletlarni keyingi o'yinga surib yuborardi.
 */
export function useSetDrawLive(): UseMutationResult<
  AdminDrawDetail,
  Error,
  { drawId: string; enabled: boolean }
> {
  const refresh = useRefreshDraws();
  return useMutation({
    mutationFn: ({ drawId, enabled }: { drawId: string; enabled: boolean }) =>
      adminRequest(`/admin/draws/${drawId}/live`, {
        method: 'POST',
        body: { enabled },
        schema: adminDrawDetailSchema,
      }),
    onSuccess: refresh,
  });
}

/**
 * G'oliblarni butun bazaga e'lon qilish.
 *
 * `onSettled` emas, `onSuccess` ham emas — IKKALASI ham refresh: muvaffaqiyatda `winnersAnnouncedAt`
 * to'ladi va tugma yopiladi, xatoda esa sabab ko'pincha "boshqa birov allaqachon bosdi" bo'ladi,
 * ya'ni ekrandagi holat baribir eskirgan.
 */
export function useAnnounceWinners(
  drawId: string,
): UseMutationResult<AnnounceWinnersResponse, Error, void> {
  const refresh = useRefreshDraws();
  return useMutation({
    mutationFn: () =>
      adminRequest(`/admin/draws/${drawId}/announce-winners`, {
        method: 'POST',
        schema: announceWinnersResponseSchema,
      }),
    onSettled: refresh,
  });
}

export function useRevealStep(
  drawId: string,
): UseMutationResult<
  DrawRevealStep,
  Error,
  { kind: DrawRevealKind; payload?: Record<string, unknown>; expectedLastSeq: number }
> {
  const refresh = useRefreshDraws();
  return useMutation({
    mutationFn: ({ kind, payload, expectedLastSeq }) =>
      adminRequest(`/admin/draws/${drawId}/reveal`, {
        method: 'POST',
        body: { kind, payload: payload ?? {}, expectedLastSeq },
        schema: drawRevealStepSchema,
      }),
    // A stale optimistic-concurrency refusal must refresh just as aggressively as success.
    onSettled: refresh,
  });
}

/** Creates a short-lived, draw-scoped credential for an OBS browser source. */
export function useBroadcastOverlayAccess(
  drawId: string,
): UseMutationResult<BroadcastOverlayAccess, Error, void> {
  return useMutation({
    mutationFn: () =>
      adminRequest(`/admin/draws/${drawId}/overlay-access`, {
        method: 'POST',
        schema: broadcastOverlayAccessSchema,
      }),
  });
}

// ---------------------------------------------------------------------------
// P4.2b — moderatsiya, DLQ, foydalanuvchi qidiruvi, hisoblar
// ---------------------------------------------------------------------------

/**
 * Ro'yxatlar "yana ko'rsatish" bilan yuriladi, `useInfiniteQuery` bilan emas.
 *
 * Sabab: kursor har bir ro'yxatda bir xil ma'noga ega emas (DLQ da OFSET), va moderatsiya
 * ekranida filtr o'zgarishi bilan butun sahifalash qaytadan boshlanadi. Bitta `cursor` holati
 * bilan sodda `useQuery` shu xatti-harakatni ochiq ko'rsatadi.
 */
export const MODERATION_RECEIPTS_KEY = ['admin', 'receipts'] as const;
export const MODERATION_PHOTOS_KEY = ['admin', 'photo-scans'] as const;
export const FRAUD_QUEUE_KEY = ['admin', 'fraud-review'] as const;
export const DLQ_KEY = ['admin', 'dlq'] as const;
export const NOTIFICATIONS_KEY = ['admin', 'notifications'] as const;
export const ACCOUNTS_KEY = ['admin', 'accounts'] as const;

function toQuery(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const query = search.toString();
  return query === '' ? '' : `?${query}`;
}

export interface ReceiptFilterState {
  status?: string;
  userId?: string;
  from?: string;
  to?: string;
}

export function useAdminReceipts(
  filter: ReceiptFilterState & OffsetPaginationQuery,
): UseQueryResult<AdminReceiptListResponse> {
  return useQuery({
    queryKey: [...MODERATION_RECEIPTS_KEY, filter],
    queryFn: () =>
      adminRequest(`/admin/receipts${toQuery({ ...filter })}`, {
        schema: adminReceiptListResponseSchema,
      }),
    placeholderData: (previous) => previous,
  });
}

export function useAdminPhotoScans(
  filter: { status?: string; subject?: string; userId?: string } & OffsetPaginationQuery,
): UseQueryResult<AdminPhotoListResponse> {
  return useQuery({
    queryKey: [...MODERATION_PHOTOS_KEY, filter],
    queryFn: () =>
      adminRequest(`/admin/photo-scans${toQuery({ ...filter })}`, {
        schema: adminPhotoListResponseSchema,
      }),
    placeholderData: (previous) => previous,
  });
}

export function useFraudQueue(
  page: OffsetPaginationQuery,
): UseQueryResult<AdminFraudTicketListResponse> {
  return useQuery({
    queryKey: [...FRAUD_QUEUE_KEY, page],
    queryFn: () =>
      adminRequest(`/admin/tickets/fraud-review${toQuery({ ...page })}`, {
        schema: adminFraudTicketListResponseSchema,
      }),
    placeholderData: (previous) => previous,
  });
}

export function useModerateTicket(): UseMutationResult<
  TicketModerationResponse,
  Error,
  { ticketId: string } & TicketModerationRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, ...body }: { ticketId: string } & TicketModerationRequest) =>
      adminRequest(`/admin/tickets/${ticketId}/moderate`, {
        method: 'POST',
        body,
        schema: ticketModerationResponseSchema,
      }),
    // Navbat ham, chek ro'yxati ham qayta o'qiladi: qaror ikkalasida ham ko'rinadi.
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: FRAUD_QUEUE_KEY });
      await queryClient.invalidateQueries({ queryKey: MODERATION_RECEIPTS_KEY });
    },
  });
}

export function useDlq(page: OffsetPaginationQuery): UseQueryResult<DlqListResponse> {
  return useQuery({
    queryKey: [...DLQ_KEY, page],
    queryFn: () =>
      adminRequest(`/admin/dlq${toQuery({ ...page })}`, { schema: dlqListResponseSchema }),
    placeholderData: (previous) => previous,
  });
}

export function useRetryFailedReceipts(): UseMutationResult<
  AdminRetryFailedReceiptsResponse,
  Error,
  AdminRetryFailedReceiptsRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminRetryFailedReceiptsRequest) =>
      adminRequest('/admin/receipts/retry', {
        method: 'POST',
        body,
        schema: adminRetryFailedReceiptsResponseSchema,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: MODERATION_RECEIPTS_KEY });
      await queryClient.invalidateQueries({ queryKey: DLQ_KEY });
      await queryClient.invalidateQueries({ queryKey: STATS_KEY });
    },
  });
}

export function useAdminNotifications(
  filter: AdminNotificationFilter & OffsetPaginationQuery,
): UseQueryResult<AdminNotificationListResponse> {
  return useQuery({
    queryKey: [...NOTIFICATIONS_KEY, filter],
    queryFn: () =>
      adminRequest(
        // `stuck` mantiqiy qiymat, `toQuery` esa faqat satr/son biladi. `true` bo'lgandagina
        // qo'shiladi: `stuck=false` ni yuborish serverda `z.coerce.boolean()` uchun ROST
        // bo'lardi — bo'sh bo'lmagan har qanday satr kabi — ya'ni filtrni o'chirish uni
        // yoqib qo'yardi.
        `/admin/notifications${toQuery({ ...filter, stuck: filter.stuck ? 'true' : undefined })}`,
        { schema: adminNotificationListResponseSchema },
      ),
    placeholderData: (previous) => previous,
  });
}

/**
 * Sanoqlar ro'yxatdan ALOHIDA so'raladi: ro'yxat sahifalangan, ya'ni "jami nechta qotgan"
 * degan raqam undan chiqmaydi — va aynan shu raqam operatorni sahifaga olib keladi.
 */
export function useNotificationStats(): UseQueryResult<AdminNotificationStats> {
  return useQuery({
    queryKey: [...NOTIFICATIONS_KEY, 'stats'],
    queryFn: () =>
      adminRequest('/admin/notifications/stats', { schema: adminNotificationStatsSchema }),
    refetchInterval: 30_000,
  });
}

export function useRetryNotifications(): UseMutationResult<
  AdminRetryNotificationsResponse,
  Error,
  AdminRetryNotificationsRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminRetryNotificationsRequest) =>
      adminRequest('/admin/notifications/retry', {
        method: 'POST',
        body,
        schema: adminRetryNotificationsResponseSchema,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    },
  });
}

/**
 * Detail sahifalar — har biri o'z marshrutida.
 *
 * Kalitlar ro'yxat kalitlarining ICHIDA (`['admin','receipts', id]`): moderatsiya qarori
 * ro'yxatni bekor qilganda ochiq turgan detail ham qayta o'qiladi, ya'ni ikkala ekran bir xil
 * haqiqatni ko'rsatadi.
 */
export function useReceiptDetail(receiptId: string): UseQueryResult<AdminReceiptDetail> {
  return useQuery({
    queryKey: [...MODERATION_RECEIPTS_KEY, receiptId],
    queryFn: () =>
      adminRequest(`/admin/receipts/${receiptId}`, { schema: adminReceiptDetailSchema }),
  });
}

export function usePhotoDetail(photoScanId: string): UseQueryResult<AdminPhotoDetail> {
  return useQuery({
    queryKey: [...MODERATION_PHOTOS_KEY, photoScanId],
    queryFn: () =>
      adminRequest(`/admin/photo-scans/${photoScanId}`, { schema: adminPhotoDetailSchema }),
  });
}

export function useTicketDetail(ticketId: string): UseQueryResult<AdminTicketDetail> {
  return useQuery({
    queryKey: [...FRAUD_QUEUE_KEY, ticketId],
    queryFn: () => adminRequest(`/admin/tickets/${ticketId}`, { schema: adminTicketDetailSchema }),
  });
}

export const USERS_KEY = ['admin', 'users'] as const;

/**
 * Foydalanuvchilar ro'yxati — ekran ochilishi bilan so'raladi.
 *
 * Ilgari bu so'rov `enabled` bilan to'silgan edi, chunki server bo'sh `query` ni rad etardi.
 * Kontrakt o'zgardi: bo'sh `query` endi "hammasi" degani, ya'ni to'siq ham kerak emas —
 * operator ekranni ochishi bilan ro'yxatni ko'radi, qidiruv esa uni toraytiradi.
 */
export function useUserList(
  filter: { query?: string } & OffsetPaginationQuery,
): UseQueryResult<AdminUserSearchResponse> {
  return useQuery({
    queryKey: [...USERS_KEY, 'list', filter],
    queryFn: () =>
      adminRequest(`/admin/users${toQuery({ ...filter })}`, {
        schema: adminUserSearchResponseSchema,
      }),
    placeholderData: (previous) => previous,
  });
}

/**
 * Foydalanuvchi kartochkasi.
 *
 * `refetchOnWindowFocus` O'CHIRILGAN va `staleTime` uzun: bu so'rov serverda audit yozuvi
 * qoldiradi. Server tomonda takrorlar deduplikatsiya qilinadi
 * (`ADMIN_USER_LOOKUP_AUDIT_WINDOW_MS`), ammo bekorga so'rov yuborish baribir shovqin —
 * boshqa oynaga o'tib qaytish "yangi murojaat" emas.
 */
export function useUserDetail(userId: string): UseQueryResult<AdminUserDetail> {
  return useQuery({
    queryKey: [...USERS_KEY, 'detail', userId],
    queryFn: () => adminRequest(`/admin/users/${userId}`, { schema: adminUserDetailSchema }),
    staleTime: ADMIN_USER_LOOKUP_AUDIT_WINDOW_MS,
    refetchOnWindowFocus: false,
  });
}

export function useAdminAccounts(): UseQueryResult<AdminAccountListResponse> {
  return useQuery({
    queryKey: ACCOUNTS_KEY,
    queryFn: () => adminRequest('/admin/accounts', { schema: adminAccountListResponseSchema }),
  });
}

export function useSetAccountActive(): UseMutationResult<
  AdminAccountRow,
  Error,
  { adminId: string; isActive: boolean; reason?: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      adminId,
      isActive,
      reason,
    }: {
      adminId: string;
      isActive: boolean;
      reason?: string;
    }) =>
      adminRequest(`/admin/accounts/${adminId}/active`, {
        method: 'POST',
        body: { isActive, reason },
        schema: adminAccountRowSchema,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNTS_KEY });
    },
  });
}

// ---------------------------------------------------------------------------
// P8 — ball-katalog (Ball-katalog)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Foydalanuvchi ustidagi qo'lda amallar
// ---------------------------------------------------------------------------

/**
 * Uchala amal ham kartochkani VA ro'yxatni bekor qiladi.
 *
 * Balans, blok holati va biletlar ikkala ekranda ham ko'rinadi. Faqat kartochkani yangilash
 * ro'yxatni eskirgan holda qoldirardi — operator esa ko'pincha kartochkadan ro'yxatga
 * qaytadi va o'zi hozirgina o'zgartirgan qatorni eski holida ko'rardi.
 *
 * Statistika ham bekor qilinadi: bloklanganlar soni dashboardda turadi.
 */
function useRefreshUser(userId: string): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [...USERS_KEY, 'detail', userId] }),
      queryClient.invalidateQueries({ queryKey: [...USERS_KEY, 'list'] }),
      queryClient.invalidateQueries({ queryKey: STATS_KEY }),
    ]);
  };
}

export function useBlockUser(
  userId: string,
): UseMutationResult<AdminUserSummary, Error, AdminUserBlockRequest> {
  const refresh = useRefreshUser(userId);
  return useMutation({
    mutationFn: (body: AdminUserBlockRequest) =>
      adminRequest(`/admin/users/${userId}/block`, {
        method: 'POST',
        body,
        schema: adminUserSummarySchema,
      }),
    onSuccess: refresh,
  });
}

export function useAdjustPoints(
  userId: string,
): UseMutationResult<AdminPointsAdjustResult, Error, AdminUserPointsRequest> {
  const refresh = useRefreshUser(userId);
  return useMutation({
    mutationFn: (body: AdminUserPointsRequest) =>
      adminRequest(`/admin/users/${userId}/points`, {
        method: 'POST',
        body,
        schema: pointsAdjustResultSchema,
      }),
    onSuccess: refresh,
  });
}

export function useGrantTicket(
  userId: string,
): UseMutationResult<AdminGrantedTicket, Error, AdminTicketGrantRequest> {
  const refresh = useRefreshUser(userId);
  return useMutation({
    mutationFn: (body: AdminTicketGrantRequest) =>
      adminRequest(`/admin/users/${userId}/tickets`, {
        method: 'POST',
        body,
        schema: grantedTicketSchema,
      }),
    onSuccess: refresh,
  });
}

/**
 * Rasm bo'yicha qaror.
 *
 * Javob — sahifaning yangi holati, ya'ni uni keshga TO'G'RIDAN-TO'G'RI yozamiz. Bekor qilib
 * qayta so'rash rasm havolasini ham qaytadan imzolatardi, holbuki ekrandagi rasm o'zgarmagan.
 */
export function useModeratePhoto(
  photoScanId: string,
): UseMutationResult<AdminPhotoDetail, Error, AdminPhotoModerationRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminPhotoModerationRequest) =>
      adminRequest(`/admin/photo-scans/${photoScanId}/moderate`, {
        method: 'POST',
        body,
        schema: adminPhotoDetailSchema,
      }),
    onSuccess: async (detail) => {
      queryClient.setQueryData([...MODERATION_PHOTOS_KEY, photoScanId], detail);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: MODERATION_PHOTOS_KEY }),
        queryClient.invalidateQueries({ queryKey: STATS_KEY }),
      ]);
    },
  });
}

export const STATS_KEY = ['admin', 'stats'] as const;

/**
 * Dashboard raqamlari.
 *
 * `staleTime` server keshi bilan bir xil: panel javobni undan tez-tez so'rasa, u baribir
 * o'sha keshdan bir xil javobni olardi — ya'ni tarmoq so'rovi shunchaki bekorga ketardi.
 */
export function useAdminStats(): UseQueryResult<AdminStats> {
  return useQuery({
    queryKey: STATS_KEY,
    queryFn: () => adminRequest('/admin/stats', { schema: adminStatsSchema }),
    staleTime: ADMIN_STATS_CACHE_SECONDS * 1000,
  });
}

export const REWARDS_KEY = ['admin', 'rewards'] as const;

export function useAdminRewards(): UseQueryResult<AdminRewardListResponse> {
  return useQuery({
    queryKey: REWARDS_KEY,
    queryFn: () => adminRequest('/admin/rewards', { schema: adminRewardListResponseSchema }),
  });
}

/** Har bir mutatsiyadan keyin ro'yxat qayta o'qiladi — pozitsiya va holat serverda hal qilinadi. */
function useRefreshRewards(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: REWARDS_KEY });
  };
}

export function useCreateReward(): UseMutationResult<AdminRewardItem, Error, AdminRewardCreate> {
  const refresh = useRefreshRewards();
  return useMutation({
    mutationFn: (body: AdminRewardCreate) =>
      adminRequest('/admin/rewards', { method: 'POST', body, schema: adminRewardItemSchema }),
    onSuccess: refresh,
  });
}

export function useUpdateReward(): UseMutationResult<
  AdminRewardItem,
  Error,
  { id: string } & AdminRewardUpdate
> {
  const refresh = useRefreshRewards();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & AdminRewardUpdate) =>
      adminRequest(`/admin/rewards/${id}`, {
        method: 'PATCH',
        body,
        schema: adminRewardItemSchema,
      }),
    onSuccess: refresh,
  });
}

export function useDeleteReward(): UseMutationResult<void, Error, string> {
  const refresh = useRefreshRewards();
  return useMutation({
    mutationFn: async (id: string) => {
      await adminRequest(`/admin/rewards/${id}`, { method: 'DELETE' });
    },
    onSuccess: refresh,
  });
}

// ---------------------------------------------------------------------------
// Sovg'a buyurtmalari (P8.1)
// ---------------------------------------------------------------------------

export const REWARD_REDEMPTIONS_KEY = ['admin', 'reward-redemptions'] as const;

export function useRewardRedemptions(
  filter: AdminRewardRedemptionFilter & OffsetPaginationQuery,
): UseQueryResult<AdminRewardRedemptionListResponse> {
  return useQuery({
    queryKey: [...REWARD_REDEMPTIONS_KEY, filter],
    queryFn: () =>
      adminRequest(`/admin/reward-redemptions${toQuery(filter)}`, {
        schema: adminRewardRedemptionListResponseSchema,
      }),
    placeholderData: (previous) => previous,
  });
}

/**
 * Sanoqlar ro'yxatdan ALOHIDA: ro'yxat sahifalangan, ya'ni "jami nechta kutmoqda" degan raqam
 * undan chiqmaydi — va aynan shu raqam operatorni bu sahifaga olib keladi.
 */
export function useRewardRedemptionStats(): UseQueryResult<AdminRewardRedemptionStats> {
  return useQuery({
    queryKey: [...REWARD_REDEMPTIONS_KEY, 'stats'],
    queryFn: () =>
      adminRequest('/admin/reward-redemptions/stats', {
        schema: adminRewardRedemptionStatsSchema,
      }),
  });
}

/**
 * Yetkazish va bekor qilish bitta hookdan: ikkalasi ham AYNAN o'sha uch so'rovni eskirtiradi
 * (ro'yxat, sanoqlar, boshqaruv paneli), va ularni ikki joyda sanash ertami-kechmi bittasini
 * unutish degani bo'lardi.
 */
function useRedemptionDecision<TBody>(
  action: 'deliver' | 'cancel',
): UseMutationResult<void, Error, { id: string } & TBody> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string } & TBody) => {
      await adminRequest(`/admin/reward-redemptions/${id}/${action}`, { method: 'POST', body });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: REWARD_REDEMPTIONS_KEY });
      // Bekor qilish ballni qaytaradi, ya'ni foydalanuvchi kartochkasidagi balans ham eskiradi.
      await queryClient.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

export function useDeliverRedemption(): UseMutationResult<
  void,
  Error,
  { id: string } & AdminDeliverRewardRedemptionRequest
> {
  return useRedemptionDecision<AdminDeliverRewardRedemptionRequest>('deliver');
}

export function useCancelRedemption(): UseMutationResult<
  void,
  Error,
  { id: string } & AdminCancelRewardRedemptionRequest
> {
  return useRedemptionDecision<AdminCancelRewardRedemptionRequest>('cancel');
}

// ---------------------------------------------------------------------------
// Admin rasm yuklash — banner va ball-katalog rasmlari umumiy
// ---------------------------------------------------------------------------

export interface UploadAdminImageInput {
  file: File;
  kind: AdminUploadKind;
}

/**
 * Rasm yuklash ikki bosqichda (§4.8 bilan bir xil intizom): avval `POST` orqali qisqa muddatli
 * yuklash havolasi so'raladi, so'ng bayt to'g'ridan-to'g'ri saqlash joyiga (MinIO) `PUT` bilan
 * yuboriladi — API rasm baytlarini hech qachon ko'rmaydi. Ikkinchi qadam ataylab `adminRequest`
 * DAN TASHQARIDA: u administrator tokenini emas, serverdan olingan imzolangan sarlavhalarni talab
 * qiladi.
 */
export function useUploadAdminImage(): UseMutationResult<string, Error, UploadAdminImageInput> {
  return useMutation({
    mutationFn: async ({ file, kind }: UploadAdminImageInput) => {
      const slot = await adminRequest('/admin/uploads/image', {
        method: 'POST',
        body: {
          kind,
          contentType: file.type,
          contentLength: file.size,
        },
        schema: adminUploadResponseSchema,
      });

      let response: Response;
      try {
        response = await fetch(slot.uploadUrl, {
          method: 'PUT',
          headers: slot.uploadHeaders,
          body: file,
        });
      } catch {
        throw new AdminApiError('NETWORK_ERROR', 0);
      }
      if (!response.ok) {
        throw new AdminApiError('UPLOAD_FAILED', response.status);
      }

      return slot.key;
    },
  });
}

// ---------------------------------------------------------------------------
// Bannerlar — bosh ekrandagi banner-slayder (§ home banner)
// ---------------------------------------------------------------------------

export const BANNERS_KEY = ['admin', 'banners'] as const;

export function useAdminBanners(): UseQueryResult<AdminBannerListResponse> {
  return useQuery({
    queryKey: BANNERS_KEY,
    queryFn: () => adminRequest('/admin/banners', { schema: adminBannerListResponseSchema }),
  });
}

/** Har bir mutatsiyadan keyin ro'yxat qayta o'qiladi — pozitsiya va holat serverda hal qilinadi. */
function useRefreshBanners(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: BANNERS_KEY });
  };
}

export function useCreateBanner(): UseMutationResult<AdminBanner, Error, AdminBannerCreate> {
  const refresh = useRefreshBanners();
  return useMutation({
    mutationFn: (body: AdminBannerCreate) =>
      adminRequest('/admin/banners', { method: 'POST', body, schema: adminBannerSchema }),
    onSuccess: refresh,
  });
}

export function useUpdateBanner(): UseMutationResult<
  AdminBanner,
  Error,
  { id: string } & AdminBannerUpdate
> {
  const refresh = useRefreshBanners();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & AdminBannerUpdate) =>
      adminRequest(`/admin/banners/${id}`, {
        method: 'PATCH',
        body,
        schema: adminBannerSchema,
      }),
    onSuccess: refresh,
  });
}

export function useDeleteBanner(): UseMutationResult<void, Error, string> {
  const refresh = useRefreshBanners();
  return useMutation({
    mutationFn: async (id: string) => {
      await adminRequest(`/admin/banners/${id}`, { method: 'DELETE' });
    },
    onSuccess: refresh,
  });
}

const DEGRADED_MODE_KEY = ['admin', 'system', 'degraded'] as const;

export function useDegradedMode(): UseQueryResult<AdminDegradedModeStatus> {
  return useQuery({
    queryKey: DEGRADED_MODE_KEY,
    queryFn: () =>
      adminRequest('/admin/system/degraded', { schema: adminDegradedModeStatusSchema }),
    refetchInterval: 30_000,
  });
}

export function useSetDegradedMode(): UseMutationResult<
  AdminDegradedModeStatus,
  Error,
  AdminSetDegradedModeRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminSetDegradedModeRequest) =>
      adminRequest('/admin/system/degraded', {
        method: 'POST',
        body,
        schema: adminDegradedModeStatusSchema,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: DEGRADED_MODE_KEY });
    },
  });
}

export const NOTIFICATION_TEMPLATES_KEY = ['admin', 'notification-templates'] as const;

export function useNotificationTemplates(): UseQueryResult<NotificationTemplateListResponse> {
  return useQuery({
    queryKey: NOTIFICATION_TEMPLATES_KEY,
    queryFn: () =>
      adminRequest('/admin/notification-templates', {
        schema: notificationTemplateListResponseSchema,
      }),
  });
}

export function useSetNotificationTemplate(): UseMutationResult<
  NotificationTemplate,
  Error,
  { key: string } & AdminUpdateNotificationTemplateRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, ...body }: { key: string } & AdminUpdateNotificationTemplateRequest) =>
      adminRequest(`/admin/notification-templates/${encodeURIComponent(key)}`, {
        method: 'PUT',
        body,
        schema: notificationTemplateSchema,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: NOTIFICATION_TEMPLATES_KEY });
    },
  });
}

/** Standartga qaytarish — bazadagi qator o'chadi, fayl/koddagi matn yana ko'rinadi. */
export function useResetNotificationTemplate(): UseMutationResult<
  NotificationTemplate,
  Error,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (key: string) =>
      adminRequest(`/admin/notification-templates/${encodeURIComponent(key)}`, {
        method: 'DELETE',
        schema: notificationTemplateSchema,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: NOTIFICATION_TEMPLATES_KEY });
    },
  });
}

/**
 * Oldindan ko'rishni SERVER render qiladi.
 *
 * `{...}` almashtirishni bu yerda takrorlash ikkinchi implementatsiya bo'lardi, va ikkitasi
 * vaqt o'tib ajralib ketadi — o'shanda admin ko'rgan narsa foydalanuvchi olgan narsa bo'lmay
 * qoladi. Shuning uchun keshlanmaydigan mutatsiya: har bosishda yangi javob.
 */
/**
 * Shablonni bitta chatga sinash uchun yuborish.
 *
 * Qoralama ham yuboriladi: saqlash SHART emas — aks holda har bir sinov jonli matnni
 * almashtirardi.
 */
export function useSendTestNotification(): UseMutationResult<
  AdminSendTestNotificationResponse,
  Error,
  { key: string } & AdminSendTestNotificationRequest
> {
  return useMutation({
    mutationFn: ({ key, ...body }: { key: string } & AdminSendTestNotificationRequest) =>
      adminRequest(`/admin/notification-templates/${encodeURIComponent(key)}/test`, {
        method: 'POST',
        body,
        schema: adminSendTestNotificationResponseSchema,
      }),
  });
}

export function usePreviewNotificationTemplate(): UseMutationResult<
  AdminPreviewNotificationTemplateResponse,
  Error,
  { key: string } & AdminUpdateNotificationTemplateRequest
> {
  return useMutation({
    mutationFn: ({ key, ...body }: { key: string } & AdminUpdateNotificationTemplateRequest) =>
      adminRequest(`/admin/notification-templates/${encodeURIComponent(key)}/preview`, {
        method: 'POST',
        body,
        schema: adminPreviewNotificationTemplateResponseSchema,
      }),
  });
}
